package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.ui.kit.FatiaDash
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.Seletor
import br.com.htms.inspeccaocampo.ui.theme.PortalCores

data class SaldoDash(val categoria: String, val cc: String, val obra: String, val qtd: Double, val situacao: String)
data class MovDash(val data: String, val tipo: String, val item: String, val qtd: Double, val cc: String, val obra: String, val destino: String)
data class PatDash(val posse: String, val cc: String, val obra: String, val situacao: String)

data class EstoqueDash(
    val itens: Int,
    val centros: List<Pair<String, String>>,
    val saldos: List<SaldoDash>,
    val movs: List<MovDash>,
    val pats: List<PatDash>
)

suspend fun carregarEstoqueDash(): Result<EstoqueDash> =
    AuthApi.apiJson("GET", "/estoque/dados").mapCatching(::parseEstoqueDash)

fun parseEstoqueDash(raw: String): EstoqueDash {
    val o = JsonMap.parse(raw)
    fun obj(a: Any?) = a as? JsonMap
    val nomes = o.array("itens").orEmpty().mapNotNull { obj(it) }.associateBy { it.str("id") }
    return EstoqueDash(
        itens = nomes.size,
        centros = o.array("centros").orEmpty().mapNotNull { obj(it)?.let { j -> j.str("codigo") to (j.str("codigo") + " - " + j.str("nome")).trim(' ', '-') } },
        saldos = o.array("saldos").orEmpty().mapNotNull { obj(it)?.let { j ->
            val item = nomes[j.str("item_id")]
            SaldoDash(item?.str("categoria") ?: "", j.str("cc_codigo"), j.str("obra_nome"), j.double("qtd") ?: 0.0, j.str("situacao"))
        } },
        movs = o.array("movs").orEmpty().mapNotNull { obj(it)?.let { j ->
            val dest = listOf(j.str("cc_destino"), j.str("obra_destino")).filter { it.isNotBlank() }.joinToString(" - ")
            MovDash(j.str("data"), j.str("tipo"), j.str("item_nome"), j.double("qtd") ?: 0.0, j.str("cc_codigo"), j.str("obra_nome"), dest)
        } },
        pats = o.array("patrimonio").orEmpty().mapNotNull { obj(it)?.let { j ->
            PatDash(j.str("posse"), j.str("cc_codigo"), j.str("obra_nome"), j.str("situacao"))
        } }
    )
}

private fun rotuloCat(c: String) = when (c) {
    "EPI" -> "EPI"
    "MOVEL" -> "Móvel"
    "INFORMATICA" -> "Informática"
    "MATERIAL_LT" -> "Material de linha"
    "MAQUINA" -> "Máquina"
    "", "OUTRO" -> "Outro"
    else -> c
}

private fun rotuloTipo(t: String) = when (t) {
    "ENTRADA" -> "Entrada"
    "SAIDA" -> "Saída"
    "TRANSFERENCIA" -> "Transferência"
    else -> t.ifBlank { "Outro" }
}

private fun rotuloPosse(p: String) = when (p.uppercase()) {
    "PROPRIO" -> "Próprio"
    "ALUGADO" -> "Alugado"
    else -> p.ifBlank { "Sem posse" }
}

private fun rotuloObra(cc: String, obra: String) = when {
    cc.isBlank() && obra.isBlank() -> "Sem obra"
    obra.isBlank() -> cc
    cc.isBlank() -> obra
    else -> "$cc · $obra"
}

@Composable
fun DashboardEstoque(d: EstoqueDash, carregando: Boolean = false, onAtualizar: (() -> Unit)? = null) {
    var cc by remember { mutableStateOf("") }
    val saldos = d.saldos.filter { cc.isBlank() || it.cc == cc }
    val movs = d.movs.filter { cc.isBlank() || it.cc == cc || it.destino.startsWith(cc) }
    val pats = d.pats.filter { cc.isBlank() || it.cc == cc }
    val obras = saldos.map { it.cc }.filter { it.isNotBlank() }.distinct().size
    val zerados = saldos.count { it.qtd <= 0 }
    val qtd = saldos.sumOf { it.qtd }
    val centros = (d.centros + d.saldos.map { it.cc }.filter { it.isNotBlank() }.distinct().map { cod ->
        val obra = d.saldos.firstOrNull { it.cc == cod }?.obra.orEmpty()
        cod to if (obra.isBlank()) cod else "$cod - $obra"
    }).distinctBy { it.first }.sortedBy { it.second }
    val nomeObra = saldos.groupBy { it.cc }.mapValues { (cod, ls) -> rotuloObra(cod, ls.firstOrNull { it.obra.isNotBlank() }?.obra.orEmpty()) }
    val idPorNome = nomeObra.entries.associate { (cod, nome) -> nome to cod }
    PainelDashboard(
        titulo = "Dashboard — Estoque",
        subtitulo = if (cc.isBlank()) "Saldos, patrimônio e movimentos de todas as obras" else centros.firstOrNull { it.first == cc }?.second ?: cc,
        carregando = carregando,
        onAtualizar = onAtualizar,
        filtros = {
            Box(Modifier.widthIn(min = 260.dp, max = 520.dp)) {
                Seletor("Centro de custo", listOf("" to "Todas as obras") + centros, cc, { cc = it }, embutido = true)
            }
        }
    ) { largo, grande ->
        PaineisDoDash("estoque", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(largo, listOf(
            { m -> KpiDash("Itens", FmtDash.qtd(d.itens), m, cor = PortalCores.Navy, legenda = "No cadastro") },
            { m -> KpiDash("Linhas de saldo", FmtDash.qtd(saldos.size), m, cor = PortalCores.AzulIcone, legenda = "Item por obra") },
            { m -> KpiDash("Obras", FmtDash.qtd(obras), m, cor = PortalCores.Navy, legenda = "Com saldo") },
            { m -> KpiDash("Quantidade", FmtDash.qtd(qtd), m, cor = PortalCores.Verde, legenda = "Soma dos saldos") },
            { m -> KpiDash("Zerados", FmtDash.qtd(zerados), m, cor = PortalCores.Vermelho, legenda = "Saldo zero ou negativo", destaque = zerados > 0) },
            { m -> KpiDash("Patrimônio", FmtDash.qtd(pats.size), m, cor = PortalCores.Laranja, legenda = "Equipamentos e ferramentas") }
        ))
            }),
            ItemPainel("categoria", "Saldo por categoria") { m ->
                GraficoRosca(
                    "Saldo por categoria", "Linhas de saldo",
                    contagem(saldos) { rotuloCat(it.categoria) }.mapIndexed { i, (k, n) -> FatiaDash(k, n, CoresDashDe(i)) },
                    m, grande, rotuloTotal = "linhas"
                )
            },
            ItemPainel("situacao", "Situação do saldo") { m ->
                GraficoRosca(
                    "Situação do saldo", "Como o item está na obra",
                    contagem(saldos) { it.situacao.ifBlank { "Sem situação" } }.mapIndexed { i, (k, n) -> FatiaDash(k, n, CoresDashDe(i + 2)) },
                    m, grande, rotuloTotal = "linhas"
                )
            },
            ItemPainel("qtd-obra", "Quantidade por obra", inteiro = true) { m ->
                RankingBarras(
                    "Quantidade por obra", "Soma do saldo · clique para filtrar",
                    saldos.groupBy { nomeObra[it.cc] ?: rotuloObra(it.cc, it.obra) }.map { (k, ls) -> k to ls.sumOf { it.qtd } }.sortedByDescending { it.second },
                    m,
                    selecionado = nomeObra[cc],
                    onClick = { nome ->
                        val id = idPorNome[nome].orEmpty()
                        cc = if (cc == id) "" else id
                    }
                )
            },
            ItemPainel("movimentos", "Movimentos") { m ->
                GraficoRosca(
                    "Movimentos", "Entrada, saída e transferência",
                    contagem(movs) { rotuloTipo(it.tipo) }.map { (k, n) ->
                        val cor = when (k) {
                            "Entrada" -> PortalCores.Verde
                            "Saída" -> PortalCores.Vermelho
                            else -> PortalCores.Navy
                        }
                        FatiaDash(k, n, cor)
                    },
                    m, grande, rotuloTotal = "lanç."
                )
            },
            ItemPainel("patrimonio", "Patrimônio") { m ->
                GraficoRosca(
                    "Patrimônio", "Próprio e alugado",
                    contagem(pats) { rotuloPosse(it.posse) }.mapIndexed { i, (k, n) ->
                        FatiaDash(k, n, if (k == "Alugado") PortalCores.Laranja else CoresDashDe(i))
                    },
                    m, grande, rotuloTotal = "itens"
                )
            },
            ItemPainel("pat-obra", "Patrimônio por obra", inteiro = true) { m ->
                RankingBarras(
                    "Patrimônio por obra", "Equipamentos e ferramentas",
                    pats.groupBy { rotuloObra(it.cc, it.obra) }.map { (k, ls) -> k to ls.size.toDouble() }.sortedByDescending { it.second },
                    m, cor = PortalCores.Laranja
                )
            }
        ))
    }
}

private fun CoresDashDe(i: Int) = br.com.htms.inspeccaocampo.ui.kit.CoresDash.cor(i)
